import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface ShadowAiDiscovery {
  id: string;
  service_id: string;
  service_name: string;
  service_category: string;
  user_email?: string;
  department?: string;
  status: string;
  risk_level: string;
  usage_count: number;
  last_detected_at: string;
  sensitive_data_types: string[];
}

interface RiskSummary {
  data: {
    total_discoveries: number;
    unique_users: number;
    total_usage_events: number;
    by_risk: Record<string, number>;
    by_status: Record<string, number>;
  };
}

interface ListResponse {
  data: ShadowAiDiscovery[];
  hasMore?: boolean;
}

export function ShadowAIPage() {
  const { data: listData, loading, error, refresh } = useApi<ListResponse>('/protect/shadow-ai');
  const { data: summaryData } = useApi<RiskSummary>('/protect/shadow-ai?action=risk_summary');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const discoveries = listData?.data ?? [];
  const summary = summaryData?.data;

  async function handleApprove(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/shadow-ai/${id}?action=approve`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleBlock(id: string) {
    setActionLoading(id);
    try {
      await post(`/protect/shadow-ai/${id}?action=block`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to block');
    } finally {
      setActionLoading(null);
    }
  }

  function policyBadge(status: string) {
    const colors =
      status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
      status === 'blocked' ? 'bg-red-100 text-red-700' :
      status === 'under_review' ? 'bg-yellow-100 text-yellow-700' :
      'bg-blue-100 text-blue-700';
    const label = status === 'detected' ? 'Detected' : status === 'approved' ? 'Allowed' : status === 'blocked' ? 'Blocked' : 'Review';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors}`}>{label}</span>;
  }

  function riskBadge(risk: string) {
    const colors =
      risk === 'critical' ? 'bg-red-100 text-red-700' :
      risk === 'high' ? 'bg-orange-100 text-orange-700' :
      risk === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      'bg-emerald-100 text-emerald-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{risk}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading shadow AI data...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Shadow AI Discovery</h2>
      <p className="text-gray-600 mb-6">Detect and manage unauthorized AI service usage across your organization.</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">AI Services Detected</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.total_discoveries ?? discoveries.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Blocked Services</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{summary?.by_status?.blocked ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Pending Review</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {(summary?.by_status?.detected ?? 0) + (summary?.by_status?.under_review ?? 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Service</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Usage</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Policy</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Risk</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Last Seen</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {discoveries.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No AI services detected yet.</td></tr>
            ) : discoveries.map((s) => (
              <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{s.service_name}</td>
                <td className="px-4 py-3 text-gray-600 capitalize">{s.service_category.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-gray-600">{s.user_email ?? '—'}</td>
                <td className="px-4 py-3 text-right text-gray-600">{s.usage_count}</td>
                <td className="px-4 py-3">{policyBadge(s.status)}</td>
                <td className="px-4 py-3">{riskBadge(s.risk_level)}</td>
                <td className="px-4 py-3 text-gray-500">{new Date(s.last_detected_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  {s.status !== 'approved' && (
                    <button
                      onClick={() => handleApprove(s.id)}
                      disabled={actionLoading === s.id}
                      className="text-xs text-emerald-600 hover:text-emerald-800 mr-2 disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {s.status !== 'blocked' && (
                    <button
                      onClick={() => handleBlock(s.id)}
                      disabled={actionLoading === s.id}
                      className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Block
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
